"use client";
import { Button } from "@workspace/ui/components";
import { cn } from "@workspace/ui/lib/utils";
import { CircleCheck } from "lucide-react";
import Link from "next/link";
import salesManagers from "@/public/animations/2-1.json";
import enablementManagers from "@/public/animations/2-2.json";
import salesOperations from "@/public/animations/2-3.json";
import cro from "@/public/animations/2-4.json";
import { LottieInView } from "@workspace/ui/common";
import { BOOK_DEMO_URL, INSTALL_EXTENSION_URL } from "@/lib/landing-links";

const ITEMS = [
  {
    title: "Sales Managers",
    animation: salesManagers,
    description:
      "Stop scrubbing transcripts. Yolk shows you a live skills map and auto-builds the drills — so you coach what actually moves the number.",
    features: [
      "Know exactly who to coach and on what, instantly",
      "Save 5+ hours/week",
      "Auto-deploy roleplays built from real calls",
      "Win back time for strategy, deals, and pipeline",
    ],
  },
  {
    title: "Enablement Managers",
    animation: enablementManagers,
    description:
      "Stop building from scratch. Yolk turns your real calls into AI roleplays — your buyers, your objections, your talk tracks — then tracks proficiency and proves the impact in revenue.",
    features: [
      "Build roleplays from real calls, automatically",
      "Track completion and proficiency per rep",
      "Prove impact in revenue, not activity",
      "Spend less time building, more time scaling",
    ],
  },
  {
    title: "Sales Operations Directors",
    animation: salesOperations,
    description:
      "One scoring standard across every manager and every call. Yolk turns thousands of real calls into skill-level signal — so you see why deals are won or lost, and ramp new hires predictably.",
    features: [
      "Consistent coaching across managers",
      "Predictable ramp times for new hires",
      "Understand why deals are won or lost",
      "Scale enablement without new headcount",
    ],
  },
  {
    title: "CROs & VPs of Sales",
    animation: cro,
    description:
      "Gong tells you why you lost. Roleplays rehearse calls that never happen. Only Yolk coaches reps live, in the call — so ramp drops, win rates climb, no new headcount.",
    features: [
      "Ramp new reps up to 5× faster",
      "Lift win rates with live, in-call coaching",
      "Cut enablement cost — automate the busywork",
      "Grow the number without growing the team",
    ],
  },
];

export const Features = () => {
  return (
    <div className="flex flex-col items-center w-full gap-10 md:gap-[72px]">
      {ITEMS.map((item, i) => (
        <div
          key={i}
          className={cn(
            "flex flex-col gap-5 max-w-[1144px] w-full lg:gap-[96px] items-center",
            i % 2 === 0 ? "md:flex-row" : "md:flex-row-reverse",
          )}
        >
          <div className="flex items-center justify-center rounded-[32px] border border-[#e3d8c4] aspect-[4/3] w-full md:max-w-[562px] bg-[#F3E4C9]">
            <LottieInView
              animationData={item.animation}
              width={562}
              height={428}
            />
          </div>
          <div className="flex flex-col w-full md:max-w-[428px] gap-6 lg:gap-10">
            <div className="flex flex-col gap-3">
              <span className="text-lg font-semibold leading-6 lg:text-[24px] lg:leading-8">
                {item.title}
              </span>
              <p className="text-sm font-medium text-d-gray lg:text-base lg:leading-[22px]">
                {item.description}
              </p>
              <ul className="list-none flex flex-col gap-3">
                {item.features.map((f, j) => (
                  <li key={j} className="flex items-center gap-2.5">
                    <CircleCheck
                      size={20}
                      className="shrink-0 [&>circle]:fill-d-green [&>circle]:stroke-d-green [&>path]:stroke-white"
                    />
                    <span className="text-sm font-medium leading-5 lg:text-[18px] lg:leading-[22px]">
                      {f}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="flex flex-col items-start gap-4 sm:flex-row sm:items-center sm:gap-4">
              <Button
                variant="d-primary"
                className="font-sfpro-rounded h-12"
                asChild
              >
                <Link
                  href={INSTALL_EXTENSION_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Install Free Extension
                </Link>
              </Button>
              <Link
                href={BOOK_DEMO_URL}
                className="text-lg font-semibold text-d-gray underline underline-offset-4 px-2 py-3"
              >
                Book A Demo
              </Link>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};
